import { useTranslation } from 'react-i18next';
import useModal from 'hooks/useModal';
import Button from 'components/atoms/Button/Button.component';
import CloseCircleIcon from 'assets/svg/icons/CloseCircle';
import { World } from 'types/shared/worlds';
import ModalHeading from 'components/molecules/ModalHeading/ModalHeading.component';
import useWalletOnboardingModal from '../useWalletOnboardingModal';
import convertMarkdown from 'utils/markdown';

const VIVID_BANK_SUPPORT_LINK = 'https://vivid.money/en-eu/support/';

const VividBlockedModalContent: React.FC<{
  onClose: () => void;
  canAccessAccountableBanking: boolean;
  hasConnectorsData: boolean;
  onOpenWalletOnboarding: () => void;
}> = ({
  onClose,
  canAccessAccountableBanking,
  hasConnectorsData,
  onOpenWalletOnboarding,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-6">
      <ModalHeading
        Icon={CloseCircleIcon}
        iconClassName="!w-10 !h-10"
        iconWrapper={false}
        title={t('banks.vivid.blocked_modal.title')}
        color={World.BANK}
      />

      {/* Content */}
      <div className="space-y-4 my-10">
        <p className="text-gray-700 leading-relaxed">
          {convertMarkdown(t('banks.vivid.blocked_modal.description'))}
        </p>

        <div className="space-y-2">
          <p className="text-gray-700">
            {t('banks.vivid.blocked_modal.help_action1')}{' '}
            <a
              className="hover:underline font-bold"
              href={VIVID_BANK_SUPPORT_LINK}
              target="_blank"
              rel="noreferrer"
            >
              {t('customers.click_here')}
            </a>
          </p>

          {canAccessAccountableBanking && !hasConnectorsData ? (
            <p className="text-gray-700">
              {t('banks.vivid.blocked_modal.help_action2')}
            </p>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      {canAccessAccountableBanking && !hasConnectorsData ? (
        <div className="flex w-full">
          <Button
            type="button"
            color={World.BANK}
            onClick={() => {
              onClose();
              onOpenWalletOnboarding();
            }}
            className="w-full"
          >
            {t('banks.vivid.blocked_modal.cta')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export type UseVividBlockedModalAPI = {
  open: () => void;
  close: () => void;
};

type UseVividBlockedModalReturn = [React.ReactNode, UseVividBlockedModalAPI];

const useVividBlockedModal = (
  canAccessAccountableBanking: boolean,
  hasConnectorsData: boolean,
): UseVividBlockedModalReturn => {
  const [modalContent, modalApi] = useModal({
    width: '2xl',
    name: 'vivid-blocked-modal',
    color: 'bank',
  });

  const [walletOnboardingModal, walletOnboardingApi] =
    useWalletOnboardingModal();

  const api: UseVividBlockedModalAPI = {
    open: () => {
      modalApi.open(() => (
        <VividBlockedModalContent
          onClose={modalApi.close}
          canAccessAccountableBanking={canAccessAccountableBanking}
          hasConnectorsData={hasConnectorsData}
          onOpenWalletOnboarding={() => walletOnboardingApi.open()}
        />
      ));
    },
    close: modalApi.close,
  };

  return [
    <>
      {modalContent}
      {walletOnboardingModal}
    </>,
    api,
  ];
};

export default useVividBlockedModal;
